// Виджет «Спросить CRM»: круглая кнопка в правом нижнем углу и панель диалога.
//
// Четыре решения, которые легко потерять при правке.
//
//  1. **Кнопка без права.** Ассистент доступен всем ролям, включая педагога, —
//     педагогов 46 из 57, и именно они спрашивают «что у меня сегодня». Гейта
//     `can()` здесь нет намеренно; сужают доступ ИНСТРУМЕНТЫ на сервере.
//  2. **`z-[55]` — выше дроверов (50), ниже RepeatGuard (100).** Главный
//     сценарий — спросить, не закрывая открытую карточку клиента. Опустите
//     ниже пятидесяти, и в этом сценарии кнопка исчезнет.
//  3. **Правый нижний угол свободен, но только он.** По центру внизу живёт
//     «Вышло обновление CRM», снизу на телефоне — панель навигации; поэтому
//     на узком экране кнопка поднята над ней вместе с safe-area.
//  4. **Все хуки — до первого `return`.** Хук после раннего выхода = React #310
//     и белый экран у всех разом (так лёг мини-апп в Telegram и MAX).
import { Bot, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { cn } from '@/lib/utils';

import { useAssistantStatus } from '../hooks/useAssistant';

import { AssistantPanel } from './AssistantPanel';

/** Открыта ли панель — переживает переход между разделами, но не вкладку. */
const OPEN_KEY = 'assistant:open';

function readOpen(): boolean {
  try {
    return sessionStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
}

export function AssistantWidget(): JSX.Element | null {
  const status = useAssistantStatus();
  const [open, setOpen] = useState(readOpen);

  useEffect(() => {
    try {
      if (open) sessionStorage.setItem(OPEN_KEY, '1');
      else sessionStorage.removeItem(OPEN_KEY);
    } catch {
      // Приватное окно и запрет хранилища: панель просто не запомнит состояние.
    }
  }, [open]);

  const close = useCallback(() => setOpen(false), []);

  // Не настроен мост — виджета нет вовсе. Показывать кнопку, которая на каждый
  // вопрос отвечает «сервис не настроен», хуже, чем не показывать её.
  if (!status.data?.enabled) return null;

  return (
    <>
      {open ? <AssistantPanel onClose={close} /> : null}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Закрыть ассистента' : 'Спросить CRM'}
        aria-expanded={open}
        className={cn(
          'fixed z-[55] flex items-center justify-center rounded-full shadow-lg transition-colors',
          // ТЕЛЕФОН: по центру, вплотную над нижней панелью. В правом углу
          // кнопка накрывала «Отправить» в мессенджере — а это единственное
          // место, где сообщение уходит клиенту (сообщение владельца 02.09.2026).
          // По центру над панелью свободно: композер занимает края, не середину.
          'left-1/2 h-10 w-10 -translate-x-1/2',
          'bottom-[calc(52px+env(safe-area-inset-bottom)+0.125rem)]',
          // КОМПЬЮТЕР: правый нижний угол, там пусто и ничего не перекрывается.
          'lg:bottom-4 lg:left-auto lg:right-4 lg:h-12 lg:w-12 lg:translate-x-0',
          // На телефоне панель открывается во весь экран и закрывается своей
          // кнопкой «Свернуть»; плавающая кнопка поверх неё только мешала бы.
          open && 'max-lg:hidden',
          open
            ? 'bg-ink-black text-canvas-white'
            : 'bg-primary text-canvas-white hover:brightness-110',
        )}
      >
        {open ? <X className="h-5 w-5" /> : <Bot className="h-5 w-5 lg:h-6 lg:w-6" />}
      </button>
    </>
  );
}
